import {UseModal} from "../../hooks/useModal";
import {Organization} from "../../app/api/model/organization";
import UpdateDialog from "../../components/Dialog/UpdateDialog";
import {
    CompanyCardDetail,
    CompanyCardField,
    CompanyCardServer,
    CompanyCardServerField,
} from "../../app/api/model/organization/companyCard";
import {getAddEditCompanyCardValidation} from "./Detail/validation";
import {InputField, InputFieldType} from "../../components/input/factory/types";
import {CompanyCardCreateRequest} from "../../app/api/types";
import {Grid} from "@mui/material";
import {LabeledValue} from "../../components/Layout/Page";
import React from "react";
import {useTranslation} from "react-i18next";
import {
    ListRequest,
    useCompanyCardCreateMutation,
    useCompanyCardServerQuery,
    useCompanyCardUpdateMutation,
} from "../../app/api/api";
import dayjs from "dayjs";

interface AddEditCompanyCardProps {
    modal: UseModal;
    organization: Organization;
    companyCard?: CompanyCardDetail;
    onClose?: () => void;
}
export function AddEditCompanyCard({modal, organization, companyCard, onClose}: AddEditCompanyCardProps) {
    const {t} = useTranslation();
    const [createCompanyCard] = useCompanyCardCreateMutation();
    const [updateCompanyCard] = useCompanyCardUpdateMutation();
    const handleSubmit = async (requestData: Partial<CompanyCardDetail>) => {
        //the last 3 digits represent the slot and the first digits the drawer number
        const slotInDrawerSelected = parseInt(`${requestData[CompanyCardField.CARD_READER_SLOT]}`, 10);
        const cardDrawerSelected = parseInt(`${requestData[CompanyCardField.CARD_READER_DRAWER]}`, 10);

        const payload = {
            [CompanyCardField.ID]: requestData[CompanyCardField.ID],
            [CompanyCardField.ORGANIZATION_ID]: organization.id,
            [CompanyCardField.CARD_ID]: requestData[CompanyCardField.CARD_ID] || null,
            [CompanyCardField.CARD_READER_SLOT]: slotInDrawerSelected,
            [CompanyCardField.CARD_READER_DRAWER]: cardDrawerSelected,
            [CompanyCardField.CARD_SERVER_ID]: requestData[CompanyCardField.CARD_SERVER_ID],
            [CompanyCardField.COMMENT]: requestData[CompanyCardField.COMMENT],
            [CompanyCardField.VALID_UNTIL]: requestData[CompanyCardField.VALID_UNTIL],
        } as CompanyCardCreateRequest;
        if (requestData[CompanyCardField.ID]) {
            await updateCompanyCard(payload).unwrap();
        } else {
            await createCompanyCard(payload).unwrap();
        }
    };

    const initialValues = {
        [CompanyCardField.ID]: companyCard?.[CompanyCardField.ID] || undefined,
        [CompanyCardField.CARD_READER_SLOT]: companyCard?.[CompanyCardField.CARD_READER_SLOT],
        [CompanyCardField.CARD_READER_DRAWER]: companyCard?.[CompanyCardField.CARD_READER_DRAWER],
        [CompanyCardField.CARD_ID]: companyCard?.[CompanyCardField.CARD_ID] as number,
        [CompanyCardField.CARD_SERVER_ID]: companyCard?.[CompanyCardField.CARD_SERVER_ID] as number,
        [CompanyCardField.COMMENT]: companyCard?.[CompanyCardField.COMMENT] || "",
        [CompanyCardField.DIVISION]: companyCard?.[CompanyCardField.DIVISION] || "",
        [CompanyCardField.ORGANIZATION_ID]: organization?.id,
        [CompanyCardField.VALID_UNTIL]: companyCard?.[CompanyCardField.VALID_UNTIL]
            ? dayjs(companyCard?.[CompanyCardField.VALID_UNTIL]).format()
            : "",
    };

    return (
        <UpdateDialog
            titleKey={companyCard ? "tooltip_editCompanyCard" : "tooltip_addCompanyCard"}
            modal={modal}
            validationSchema={getAddEditCompanyCardValidation}
            inputFields={[
                {
                    fieldName: CompanyCardField.ID,
                    type: InputFieldType.HIDDEN,
                    required: !!companyCard,
                    translationKey: t("generic_id"),
                },
                {
                    translationKey: "companyCard_division",
                    fieldName: CompanyCardField.DIVISION,
                    required: true,
                    type: InputFieldType.HIDDEN,
                },
                {
                    translationKey: "generic_organization",
                    fieldName: CompanyCardField.ORGANIZATION_ID,
                    required: true,
                    type: InputFieldType.HIDDEN,
                },
                {
                    translationKey: "companyCard_cardId",
                    fieldName: CompanyCardField.CARD_ID,
                    required: true,
                    gridProps: {xs: 12},
                },
                {
                    translationKey: "companyCard_cardDrawer",
                    fieldName: CompanyCardField.CARD_READER_DRAWER,
                    required: true,
                    type: InputFieldType.INTEGER,
                    inputProps: {min: 0, pattern: "[0-9]|1[012]", max: 12},
                },
                {
                    translationKey: "companyCard_cardDrawerSlot",
                    fieldName: CompanyCardField.CARD_READER_SLOT,
                    required: true,
                    type: InputFieldType.INTEGER,
                    inputProps: {min: 0, pattern: "[0-9]", max: 120},
                },
                {
                    translationKey: "companyCard_cardServer",
                    fieldName: CompanyCardField.CARD_SERVER_ID,
                    required: true,
                    type: InputFieldType.SELECT_FROM_REQUEST,
                    query: useCompanyCardServerQuery,
                    request: {},
                    gridProps: {xs: 6},
                    optionLabelField: CompanyCardServerField.NAME,
                } as InputField<CompanyCardDetail, CompanyCardServer, ListRequest>,
                {
                    translationKey: "companyCard_validUntil",
                    type: InputFieldType.DATE,
                    fieldName: CompanyCardField.VALID_UNTIL,
                    required: true,
                },
                {
                    translationKey: "generic_comment",
                    fieldName: CompanyCardField.COMMENT,
                    multiline: true,
                    gridProps: {xs: 12},
                    minRows: 10,
                },
            ]}
            initialValues={initialValues}
            onClose={onClose}
            onSubmit={handleSubmit}
        >
            <Grid container>
                <LabeledValue label={t("generic_organizationId")} content={organization?.name} xs={6} />
                <LabeledValue
                    label={t("organizations_dkvCustomerNumber")}
                    content={organization?.mainDKVCustomerNumber}
                    xs={6}
                />
            </Grid>
        </UpdateDialog>
    );
}
